import React, { useCallback, useMemo, useRef } from 'react';
import { addDatadogAction, addDatadogError } from '../../../utils/datadogRum';
import { usePaginationMaybe } from '../Pagination';
import { useSortingMaybe } from '../Sorting';
import { useFilteringMaybe } from '../Filtering/context';
import { useDataProvider } from '../DataProviders';
import {
  InstrumentationContextProvider,
  PendingInteraction,
  TableInstrumentationContextValue,
  TableMetricContext,
} from './context';

export type InstrumentationProviderProps = {
  /** Table identifier, typically from data-cy prop */
  tableId?: string;
  children: React.ReactNode;
};

/**
 * Internal component that provides table instrumentation.
 * Uses window.DD_RUM directly - no provider needed.
 * Works automatically in apps with DD RUM initialized (e.g., QFE).
 */
export const InstrumentationProvider: React.FC<
  InstrumentationProviderProps
> = ({ tableId, children }) => {
  const pagination = usePaginationMaybe();
  const sorting = useSortingMaybe();
  const filtering = useFilteringMaybe();
  const dataProvider = useDataProvider();
  const { data, isLoading } = dataProvider;
  const pendingInteractionRef = useRef<PendingInteraction | null>(null);

  const markInteraction = useCallback(
    (type: string, context?: Record<string, unknown>): void => {
      pendingInteractionRef.current = {
        type,
        startTime: performance.now(),
        context,
      };
    },
    [],
  );

  const consumePendingInteraction =
    useCallback((): PendingInteraction | null => {
      const pending = pendingInteractionRef.current;
      pendingInteractionRef.current = null;
      return pending;
    }, []);

  const getContext = useCallback((): TableMetricContext => {
    const baseContext: TableMetricContext = {
      row_count: data?.length ?? 0,
      page_size: pagination?.pageSize ?? null,
      page_index: pagination?.pageIndex ?? null,
      has_search_term: !!filtering?.searchTerm,
      filter_count: Object.keys(filtering?.filters ?? {}).length,
      is_sorted: !!sorting?.column,
      is_loading: isLoading,
    };

    if (tableId) {
      baseContext.table_id = tableId;
    }

    return baseContext;
  }, [
    tableId,
    data?.length,
    pagination?.pageSize,
    pagination?.pageIndex,
    filtering?.searchTerm,
    filtering?.filters,
    sorting?.column,
    isLoading,
  ]);

  const trackAction = useCallback(
    (name: string, context?: Record<string, unknown>) => {
      addDatadogAction(name, {
        ...getContext(),
        ...context,
      });
    },
    [getContext],
  );

  const trackTiming = useCallback(
    (name: string, durationMs: number, context?: Record<string, unknown>) => {
      addDatadogAction(name, {
        ...getContext(),
        duration_ms: durationMs,
        ...context,
      });
    },
    [getContext],
  );

  const trackError = useCallback(
    (error: unknown, context?: Record<string, unknown>) => {
      const sanitizedError = {
        error_type:
          error instanceof Error ? error.constructor.name : typeof error,
        error_code:
          error instanceof Error && 'code' in error
            ? (error as Error & { code?: string }).code
            : undefined,
      };

      addDatadogError(error as Error | string, {
        ...getContext(),
        ...sanitizedError,
        ...context,
      });
    },
    [getContext],
  );

  const value: TableInstrumentationContextValue = useMemo(
    () => ({
      trackAction,
      trackTiming,
      trackError,
      getContext,
      markInteraction,
      consumePendingInteraction,
    }),
    [
      trackAction,
      trackTiming,
      trackError,
      getContext,
      markInteraction,
      consumePendingInteraction,
    ],
  );

  return (
    <InstrumentationContextProvider value={value}>
      {children}
    </InstrumentationContextProvider>
  );
};
